import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from './supabaseClient';
import { nombreSeguroStorage } from './lib/nombre-storage';
import { useCompany } from './CompanyContext';
import { useImpersonation } from './ImpersonationContext';
import { logAudit } from './auditLog';
import { getErgGuideNumber } from './erg2024Data';
import UpgradeLimitModal from './components/UpgradeLimitModal';
import EnlaceArchivo from './components/EnlaceArchivo';
import { rutaSds, decisionBorradoSds, validarSds, sdsParaSubir, tieneCabeceraPdf, MENSAJE_SDS_PDF_INVALIDO } from './lib/products-sds';
import './Products.css';

import { SGA_CLASSES } from './lib/sgaClasses';
import { CLASE8_SUBCATEGORIAS } from './lib/sgaClasses';
import { COSHH_H_PHRASES, calcularPeligrosidad } from './lib/coshhEssentials';
import { ubicacionesHabilitadas } from './lib/ubicaciones-flag';
import { fetchUbicacionesDeProductos, UbicacionDescrita } from './lib/ubicaciones';
import UbicacionChips from './components/ubicaciones/UbicacionChips';
import { hoyEc, ZONA_ECUADOR } from './lib/fechas';
export { SGA_CLASSES };

// ── Types ────────────────────────────────────────────────────────────────────

interface Product {
  id: string;
  name: string;
  cas_number: string | null;
  un_number: string | null;
  provider: string | null;
  sga_classes: string[];
  sds_url: string | null;
  reactive_hazards: string | null;
  incompatible_materials: string | null;
  clase8_subcategoria: string | null;
  coshh_h_phrases: string[];
  coshh_estado_fisico: string | null;
  coshh_punto_ebullicion: number | null;
  created_at: string;
  deleted_at: string | null;
  status: 'active' | 'archived';
}

type FormData = Omit<Product, 'id' | 'created_at' | 'sds_url' | 'deleted_at' | 'status' | 'coshh_punto_ebullicion' | 'coshh_estado_fisico'> & {
  coshh_punto_ebullicion: string;
  coshh_estado_fisico: string;
};

// ── Constants ────────────────────────────────────────────────────────────────

const EMPTY_FORM: FormData = {
  name: '',
  cas_number: '',
  un_number: '',
  provider: '',
  sga_classes: [],
  reactive_hazards: '',
  incompatible_materials: '',
  clase8_subcategoria: '',
  coshh_h_phrases: [],
  coshh_estado_fisico: '',
  coshh_punto_ebullicion: '',
};

const RETENTION_DAYS = 7;
const PAGE_SIZE = 20;


// ── Helpers ───────────────────────────────────────────────────────────────────

function exportToCSV(products: Product[]) {
  const headers = ['Nombre', 'Clase IMDG', 'Número CAS', 'Número UN', 'Fecha de registro'];
  const rows = products.map(p => [
    p.name ?? '',
    p.sga_classes?.join(', ') ?? '',
    p.cas_number ?? '',
    p.un_number ?? '',
    p.created_at ? new Date(p.created_at).toLocaleDateString('es-EC') : '',
  ]);
  const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `inventario_storsafe_${hoyEc()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function daysUntilDeletion(deletedAt: string): number {
  const expiresAt = new Date(new Date(deletedAt).getTime() + RETENTION_DAYS * 86400000);
  return Math.max(0, Math.ceil((expiresAt.getTime() - Date.now()) / 86400000));
}

function deletionDateLabel(deletedAt: string): string {
  const expiresAt = new Date(new Date(deletedAt).getTime() + RETENTION_DAYS * 86400000);
  return expiresAt.toLocaleDateString('es-EC', { day: 'numeric', month: 'long', year: 'numeric', timeZone: ZONA_ECUADOR });
}

function deletionDateDDMMYYYY(deletedAt: string): string {
  const expiresAt = new Date(new Date(deletedAt).getTime() + RETENTION_DAYS * 86400000);
  const dd = String(expiresAt.getDate()).padStart(2, '0');
  const mm = String(expiresAt.getMonth() + 1).padStart(2, '0');
  const yyyy = expiresAt.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

// ── Component ────────────────────────────────────────────────────────────────

export default function Products() {
  const { company, profile } = useCompany();
  const { impersonated } = useImpersonation();
  const isViewer = profile?.role === 'viewer';
  const effectiveCompany   = impersonated ?? company;
  const effectiveCompanyId = effectiveCompany?.id ?? null;

  const [products, setProducts]             = useState<Product[]>([]);
  const [trashed, setTrashed]               = useState<Product[]>([]);
  const [loading, setLoading]               = useState(true);
  const [loadingTrash, setLoadingTrash]     = useState(false);
  const [activeTab, setActiveTab]           = useState<'active' | 'trash'>('active');
  const [modalOpen, setModalOpen]           = useState(false);
  const [editing, setEditing]               = useState<Product | null>(null);
  const [form, setForm]                     = useState<FormData>(EMPTY_FORM);
  const [saving, setSaving]                 = useState(false);
  const [error, setError]                   = useState('');
  const [search, setSearch]                 = useState('');
  const [toast, setToast]                   = useState('');
  const [sdsFile, setSdsFile]               = useState<File | null>(null);
  const fileInputRef                        = useRef<HTMLInputElement>(null);

  // Pagination, search, errors
  const [page,          setPage]          = useState(1);
  const [totalCount,    setTotalCount]    = useState(0);
  const [searchResults, setSearchResults] = useState<Product[] | null>(null);
  const [fetchError,    setFetchError]    = useState('');
  const [searchError,   setSearchError]   = useState('');
  const [trashError,    setTrashError]    = useState('');
  const searchTimerRef                    = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Soft delete confirmation state
  const [confirmTrashProduct, setConfirmTrashProduct]   = useState<Product | null>(null);
  const [trashingId, setTrashingId]                     = useState<string | null>(null);

  // Permanent delete confirmation state
  const [confirmPermProduct, setConfirmPermProduct]     = useState<Product | null>(null);
  const [permDeletingId, setPermDeletingId]             = useState<string | null>(null);

  // Restore state
  const [restoringId, setRestoringId]                   = useState<string | null>(null);

  // ── Plan limit (active + trashed-within-7-days) ───────────────────────────

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const maxProducts  = effectiveCompany?.max_products ?? null;
  const totalUsed    = totalCount + trashed.filter(p => p.deleted_at && daysUntilDeletion(p.deleted_at) > 0).length;
  const atLimit      = maxProducts !== null && totalUsed >= maxProducts;

  // ── Data fetching ──────────────────────────────────────────────────────────

  const fetchProducts = useCallback(async (pageNum = 1) => {
    setLoading(true);
    setFetchError('');
    setSearchError('');
    const from = (pageNum - 1) * PAGE_SIZE;
    const to   = from + PAGE_SIZE - 1;
    let query = supabase
      .from('products')
      .select('*', { count: 'exact' })
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .range(from, to);
    if (effectiveCompanyId) query = query.eq('company_id', effectiveCompanyId);
    const { data, error: err, count } = await query;
    if (err) setFetchError('Error al cargar productos: ' + err.message);
    else { setProducts(data ?? []); setTotalCount(count ?? 0); }
    setLoading(false);
  }, [effectiveCompanyId]);

  const fetchTrashed = useCallback(async () => {
    setLoadingTrash(true);
    let query = supabase
      .from('products')
      .select('*')
      .eq('status', 'archived')
      .order('deleted_at', { ascending: false });
    if (effectiveCompanyId) query = query.eq('company_id', effectiveCompanyId);
    const { data, error: err } = await query;
    if (err) setTrashError('Error al cargar papelera: ' + err.message);
    else { setTrashed(data ?? []); setTrashError(''); }
    setLoadingTrash(false);
  }, [effectiveCompanyId]);

  const searchProducts = useCallback(async (q: string) => {
    setLoading(true);
    setSearchError('');
    setFetchError('');
    let query = supabase
      .from('products')
      .select('*')
      .eq('status', 'active')
      .or(`name.ilike.%${q}%,cas_number.ilike.%${q}%,un_number.ilike.%${q}%`)
      .order('created_at', { ascending: false });
    if (effectiveCompanyId) query = query.eq('company_id', effectiveCompanyId);
    const { data, error: err } = await query;
    if (err) setSearchError('Error en la búsqueda: ' + err.message);
    else setSearchResults(data ?? []);
    setLoading(false);
  }, [effectiveCompanyId]);

  useEffect(() => { fetchProducts(1); fetchTrashed(); }, [fetchProducts, fetchTrashed]);

  // ── Toast helper ───────────────────────────────────────────────────────────

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!value.trim()) {
      setSearchResults(null);
      setSearchError('');
      setPage(1);
      fetchProducts(1);
      return;
    }
    searchTimerRef.current = setTimeout(() => searchProducts(value.trim()), 300);
  };

  const goToPage = (newPage: number) => {
    setPage(newPage);
    fetchProducts(newPage);
  };

  // ── Modal helpers ──────────────────────────────────────────────────────────

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setSdsFile(null);
    setError('');
    setModalOpen(true);
  };

  const openEdit = (product: Product) => {
    setEditing(product);
    setForm({
      name:        product.name,
      cas_number:  product.cas_number ?? '',
      un_number:   product.un_number  ?? '',
      provider:               product.provider               ?? '',
      sga_classes:            product.sga_classes            ?? [],
      reactive_hazards:       product.reactive_hazards       ?? '',
      incompatible_materials: product.incompatible_materials ?? '',
      clase8_subcategoria:    product.clase8_subcategoria    ?? '',
      coshh_h_phrases:        product.coshh_h_phrases        ?? [],
      coshh_estado_fisico:    product.coshh_estado_fisico    ?? '',
      coshh_punto_ebullicion: product.coshh_punto_ebullicion != null ? String(product.coshh_punto_ebullicion) : '',
    });
    setSdsFile(null);
    setError('');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setForm(EMPTY_FORM);
    setSdsFile(null);
    setError('');
  };

  // ── Form handlers ──────────────────────────────────────────────────────────

  const handleField = (field: keyof FormData, value: string) => {
    setForm((f) => ({ ...f, [field]: value }));
  };

  const toggleSGA = (id: string) => {
    setForm((f) => {
      if (id === 'no_clasificado') {
        const alreadySet = f.sga_classes.includes('no_clasificado');
        return { ...f, sga_classes: alreadySet ? [] : ['no_clasificado'] };
      }
      const withoutNoClasificado = f.sga_classes.filter((c) => c !== 'no_clasificado');
      return {
        ...f,
        sga_classes: withoutNoClasificado.includes(id)
          ? withoutNoClasificado.filter((c) => c !== id)
          : [...withoutNoClasificado, id],
      };
    });
  };

  // ── Save (insert / update + PDF upload) ────────────────────────────────────

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('El nombre es obligatorio.'); return; }
    // products.company_id es obligatorio (migración 20261014050000).
    if (!editing && !effectiveCompanyId) { setError('Selecciona una empresa antes de crear un producto.'); return; }
    const unTrimmed = form.un_number?.trim() ?? '';
    if (unTrimmed && !/^\d{4}$/.test(unTrimmed)) {
      setError('El número UN/NA debe tener exactamente 4 dígitos numéricos.');
      return;
    }
    if (sdsFile) {
      const sdsError = validarSds(sdsFile);
      if (sdsError) { setError(sdsError); return; }
      // Subir y "Cambiar PDF" pasan por aquí: además del nombre y el tipo, la cabecera %PDF (PE-A3c).
      if (!(await tieneCabeceraPdf(sdsFile))) { setError(MENSAJE_SDS_PDF_INVALIDO); return; }
    }

    setSaving(true);
    setError('');

    const payload = {
      name:        form.name.trim(),
      cas_number:  form.cas_number?.trim() || null,
      un_number:   unTrimmed || null,
      provider:               form.provider?.trim()               || null,
      sga_classes:            form.sga_classes,
      reactive_hazards:       form.reactive_hazards?.trim()       || null,
      incompatible_materials: form.incompatible_materials?.trim() || null,
      clase8_subcategoria: form.sga_classes.includes('clase8') ? (form.clase8_subcategoria || null) : null,
      coshh_h_phrases: form.coshh_h_phrases,
      coshh_estado_fisico: form.coshh_estado_fisico || null,
      coshh_punto_ebullicion: form.coshh_punto_ebullicion.trim() === '' ? null : Number(form.coshh_punto_ebullicion),
    };

    let productId: string;

    if (editing) {
      const { error: dbError } = await supabase.from('products').update(payload).eq('id', editing.id);
      if (dbError) { setError('Error al guardar: ' + dbError.message); setSaving(false); return; }
      productId = editing.id;
      if (effectiveCompanyId) logAudit({ company_id: effectiveCompanyId, action: 'updated', entity_type: 'product', entity_id: productId, entity_name: payload.name, details: { cas_number: payload.cas_number, un_number: payload.un_number, provider: payload.provider, sga_classes: payload.sga_classes } });
    } else {
      const insertPayload = { ...payload, company_id: effectiveCompanyId };
      const { data, error: dbError } = await supabase
        .from('products').insert(insertPayload).select('id').single();
      if (dbError || !data) {
        setError('Error al guardar: ' + (dbError?.message ?? 'Error desconocido'));
        setSaving(false);
        return;
      }
      productId = data.id;
      if (effectiveCompanyId) logAudit({ company_id: effectiveCompanyId, action: 'created', entity_type: 'product', entity_id: productId, entity_name: payload.name, details: { cas_number: payload.cas_number, sga_classes: payload.sga_classes } });
    }

    // Upload PDF to storage
    if (sdsFile && effectiveCompanyId) {
      const filePath = `${effectiveCompanyId}/${nombreSeguroStorage(`${productId}.pdf`)}`;
      const { error: upError } = await supabase.storage
        .from('safety-sheets')
        .upload(filePath, sdsParaSubir(sdsFile), { upsert: true, contentType: 'application/pdf' });

      if (upError) {
        setError('Producto guardado, pero error al subir PDF: ' + upError.message);
        setSaving(false);
        setPage(1);
        fetchProducts(1);
        return;
      }

      const { data: urlData } = supabase.storage.from('safety-sheets').getPublicUrl(filePath);
      const { error: urlUpdateError } = await supabase.from('products').update({ sds_url: urlData.publicUrl }).eq('id', productId);
      if (urlUpdateError) {
        setError('PDF subido, pero error al vincular URL: ' + urlUpdateError.message);
        setSaving(false);
        setPage(1);
        fetchProducts(1);
        return;
      }
    }

    setSaving(false);
    showToast(editing ? 'Producto actualizado.' : 'Producto agregado.');
    setPage(1);
    closeModal();
    fetchProducts(1);
  };

  // ── Soft delete (mover a papelera) ─────────────────────────────────────────

  const handleSoftDelete = (product: Product) => {
    setConfirmTrashProduct(product);
  };

  const confirmSoftDelete = async () => {
    if (!confirmTrashProduct) return;
    const { id, name } = confirmTrashProduct;
    setTrashingId(id);
    setConfirmTrashProduct(null);

    const { error: dbError } = await supabase
      .from('products')
      .update({ status: 'archived', deleted_at: new Date().toISOString() })
      .eq('id', id);

    if (dbError) {
      showToast('Error al mover a papelera: ' + dbError.message);
    } else {
      if (effectiveCompanyId) logAudit({ company_id: effectiveCompanyId, action: 'archived', entity_type: 'product', entity_id: id, entity_name: name });
      showToast('Producto movido a la papelera.');
      setPage(1);
      fetchProducts(1);
      fetchTrashed();
    }
    setTrashingId(null);
  };

  // ── Restore ────────────────────────────────────────────────────────────────

  const handleRestore = async (product: Product) => {
    setRestoringId(product.id);

    const { error: dbError } = await supabase
      .from('products')
      .update({ status: 'active', deleted_at: null })
      .eq('id', product.id);

    if (dbError) {
      showToast('Error al restaurar: ' + dbError.message);
    } else {
      if (effectiveCompanyId) logAudit({ company_id: effectiveCompanyId, action: 'restored', entity_type: 'product', entity_id: product.id, entity_name: product.name });
      showToast('Producto restaurado.');
      setPage(1);
      fetchProducts(1);
      fetchTrashed();
    }
    setRestoringId(null);
  };

  // ── Permanent delete ───────────────────────────────────────────────────────

  const handlePermanentDelete = (product: Product) => {
    setConfirmPermProduct(product);
  };

  const confirmPermanentDelete = async () => {
    if (!confirmPermProduct) return;
    const { id, name, sds_url } = confirmPermProduct;
    setPermDeletingId(id);
    setConfirmPermProduct(null);

    // Primero el PDF (bloque 10): si no se pudo borrar, la fila se conserva para
    // no dejar el archivo huérfano (así quedaron los 45 de la S14).
    if (sds_url && effectiveCompanyId) {
      const ruta = rutaSds(effectiveCompanyId, id, sds_url);
      const { data: removed, error: rmErr } = await supabase.storage.from('safety-sheets').remove([ruta]);
      let existeAun = false;
      if (!rmErr && (!removed || removed.length === 0)) {
        // remove() no avisa si no encontró el archivo o si no hay permiso: se mira si sigue ahí.
        const carpeta = ruta.slice(0, ruta.lastIndexOf('/'));
        const nombre = ruta.slice(ruta.lastIndexOf('/') + 1);
        const { data: lista, error: listErr } = await supabase.storage.from('safety-sheets').list(carpeta, { search: nombre });
        existeAun = !!listErr || (lista ?? []).some(o => o.name === nombre);
      }
      if (decisionBorradoSds(rmErr, removed, existeAun) === 'conservar') {
        console.error('No se pudo borrar la hoja de seguridad del almacenamiento:', rmErr ?? 'no borrada');
        showToast('No se pudo borrar la hoja de seguridad (PDF) del almacenamiento. El producto no se eliminó; intenta de nuevo.');
        setPermDeletingId(null);
        return;
      }
    }

    const { error: dbError } = await supabase.from('products').delete().eq('id', id);
    if (dbError) {
      showToast('Error al eliminar: ' + dbError.message);
    } else {
      if (effectiveCompanyId) logAudit({ company_id: effectiveCompanyId, action: 'deleted', entity_type: 'product', entity_id: id, entity_name: name });
      showToast('Producto eliminado definitivamente.');
      setTrashed((prev) => prev.filter((p) => p.id !== id));
    }
    setPermDeletingId(null);
  };

  // ── Filtered active list ──────────────────────────────────────────────────

  const isSearching     = searchResults !== null;
  const displayProducts = isSearching ? searchResults! : products;

  // Ubicación de cada producto (solo is_dev hasta que JC la libere): se carga
  // solo para los productos visibles en la página.
  const conUbicaciones = ubicacionesHabilitadas(effectiveCompany);
  const idsVisibles = displayProducts.map(p => p.id).join(',');
  const [ubicaciones, setUbicaciones] = useState<Map<string, UbicacionDescrita[]>>(new Map());
  useEffect(() => {
    if (!conUbicaciones || !idsVisibles) { setUbicaciones(new Map()); return; }
    let vigente = true;
    fetchUbicacionesDeProductos(idsVisibles.split(','))
      .then(m => { if (vigente) setUbicaciones(m); })
      .catch(() => { if (vigente) setUbicaciones(new Map()); });
    return () => { vigente = false; };
  }, [conUbicaciones, idsVisibles]);
  const totalPages      = Math.ceil(totalCount / PAGE_SIZE);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="pr-root">

      {/* ── Header row ── */}
      <div className="pr-header">
        <div>
          <h3 className="pr-title">Productos químicos</h3>
          <p className="pr-subtitle">
            {totalCount} activo{totalCount !== 1 ? 's' : ''}
            {trashed.length > 0 && ` · ${trashed.length} en papelera`}
            {maxProducts !== null && (
              <span className={`pr-limit-tag ${atLimit ? 'at-limit' : ''}`}>
                {' '}· {totalUsed}/{maxProducts} usados
              </span>
            )}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {!isViewer && (
            <button
              className="pr-btn-add"
              style={{ background: 'rgba(255,255,255,0.06)', color: '#94a3b8', border: '1px solid rgba(255,255,255,0.1)' }}
              onClick={() => exportToCSV(products)}
              disabled={products.length === 0}
              title="Exportar inventario a CSV"
            >
              <DownloadIcon /> Exportar CSV
            </button>
          )}
          {!isViewer && (
            <button
              className="pr-btn-add"
              onClick={() => { if (atLimit) setShowUpgradeModal(true); else openAdd(); }}
              disabled={activeTab === 'trash'}
              title={atLimit ? `Límite del plan alcanzado (${maxProducts} productos)` : undefined}
            >
              <PlusIcon /> Agregar Producto
            </button>
          )}
        </div>
      </div>

      {showUpgradeModal && (
        <UpgradeLimitModal
          resource="products"
          currentUsage={totalUsed}
          plan={effectiveCompany?.plan}
          onClose={() => setShowUpgradeModal(false)}
        />
      )}

      {/* ── Tabs ── */}
      <div className="pr-tabs">
        <button
          className={`pr-tab ${activeTab === 'active' ? 'active' : ''}`}
          onClick={() => setActiveTab('active')}
        >
          <ProductsTabIcon /> Productos
          <span className="pr-tab-count">{products.length}</span>
        </button>
        <button
          className={`pr-tab ${activeTab === 'trash' ? 'active' : ''}`}
          onClick={() => setActiveTab('trash')}
        >
          <TrashTabIcon /> Papelera
          {trashed.length > 0 && <span className="pr-tab-count pr-tab-count-trash">{trashed.length}</span>}
        </button>
      </div>

      {/* ── Plan limit banner ── */}
      {atLimit && activeTab === 'active' && (
        <div className="pr-limit-banner">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" />
          </svg>
          <div>
            <strong>Límite del plan alcanzado</strong>
            <p>Has alcanzado el máximo de {maxProducts} productos (activos + en papelera). Actualiza tu plan o elimina productos de la papelera para agregar más.</p>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════ */}
      {/* ── ACTIVE PRODUCTS TAB ── */}
      {/* ══════════════════════════════════════════════════════════════ */}
      {activeTab === 'active' && (
        <>
          {/* ── Search bar ── */}
          <div className="pr-search-wrap">
            <SearchIcon />
            <input
              className="pr-search-input"
              type="text"
              placeholder="Buscar por nombre, N° CAS o N° UN/NA…"
              value={search}
              onChange={e => handleSearchChange(e.target.value)}
            />
            {search && (
              <button className="pr-search-clear" onClick={() => handleSearchChange('')} title="Limpiar">
                <CloseIcon />
              </button>
            )}
          </div>

          <div className="pr-table-wrap">
            {loading ? (
              <div className="pr-loading">
                <span className="pr-spinner" />
                <p>Cargando productos…</p>
              </div>
            ) : fetchError ? (
              <div className="pr-error-box">
                <p>{fetchError}</p>
                <button className="pr-btn-retry" onClick={() => fetchProducts(page)}>Reintentar</button>
              </div>
            ) : searchError ? (
              <div className="pr-error-box">
                <p>{searchError}</p>
                <button className="pr-btn-retry" onClick={() => searchProducts(search.trim())}>Reintentar</button>
              </div>
            ) : !isSearching && displayProducts.length === 0 ? (
              <div className="pr-empty">
                <EmptyBoxIcon />
                <p className="pr-empty-title">Sin productos</p>
                <p className="pr-empty-sub">Haz clic en "Agregar Producto" para comenzar.</p>
              </div>
            ) : displayProducts.length === 0 ? (
              <div className="pr-empty">
                <EmptyBoxIcon />
                <p className="pr-empty-title">Sin resultados</p>
                <p className="pr-empty-sub">Ningún producto coincide con "{search}".</p>
              </div>
            ) : (
              <table className="pr-table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>N° CAS</th>
                    <th>N° UN/NA</th>
                    <th>Clasificación IMDG</th>
                    <th>Proveedor</th>
                    <th style={{ width: 130 }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {displayProducts.map((p) => (
                    <tr key={p.id}>
                      <td className="pr-td-name">
                        {p.name}
                        {conUbicaciones && (
                          <div style={{ marginTop: 4 }}><UbicacionChips ubicaciones={ubicaciones.get(p.id)} /></div>
                        )}
                      </td>
                      <td className="pr-td-mono">{p.cas_number || <span className="pr-empty-cell">—</span>}</td>
                      <td className="pr-td-mono">
                        <div className="pr-un-cell">
                          {p.un_number ? <span className="pr-un-badge">UN {p.un_number}</span> : <span className="pr-empty-cell">—</span>}
                          {(() => {
                            const ergNum = getErgGuideNumber(p.un_number);
                            return ergNum ? <span className="pr-erg-badge" title={`Guía ERG 2024 N° ${ergNum}`}>ERG {ergNum}</span> : null;
                          })()}
                        </div>
                      </td>
                      <td>
                        <div className="pr-badges">
                          {p.sga_classes.length === 0
                            ? <span className="pr-empty-cell">—</span>
                            : p.sga_classes.map((id) => {
                                const cls = SGA_CLASSES.find((s) => s.id === id);
                                return cls ? (
                                  <img key={id} src={cls.svg} alt={cls.label} title={cls.label}
                                    className="pr-sga-badge-img" width="40" height="40" />
                                ) : null;
                              })}
                        </div>
                      </td>
                      <td>{p.provider || <span className="pr-empty-cell">—</span>}</td>
                      <td>
                        <div className="pr-actions">
                          {p.sds_url ? (
                            <EnlaceArchivo bucket="safety-sheets" valor={p.sds_url}
                              className="pr-btn-icon pr-btn-sds" title="Ver Hoja de Seguridad (PDF)"
                              style={{ flexDirection: 'column', width: 'auto', height: 'auto', padding: '4px 8px', gap: 2 }}>
                              <EyeIcon />
                              <span style={{ fontSize: '0.62rem', lineHeight: 1 }}>Ver MSDS</span>
                            </EnlaceArchivo>
                          ) : (
                            <button
                              className="pr-sds-missing"
                              title="MSDS no disponible"
                              onClick={() => showToast('MSDS no disponible para este producto')}
                              style={{ flexDirection: 'column', width: 'auto', height: 'auto', padding: '4px 8px', gap: 2, background: 'none', border: 'none', cursor: 'pointer' }}>
                              <AlertTriangleIcon />
                              <span style={{ fontSize: '0.62rem', lineHeight: 1, color: '#f59e0b' }}>Ver MSDS</span>
                            </button>
                          )}
                          {!isViewer && (
                            <button className="pr-btn-icon pr-btn-edit" onClick={() => openEdit(p)} title="Editar">
                              <EditIcon />
                            </button>
                          )}
                          {!isViewer && (
                            <button
                              className="pr-btn-icon pr-btn-delete"
                              onClick={() => handleSoftDelete(p)}
                              disabled={trashingId === p.id}
                              title="Mover a papelera"
                            >
                              {trashingId === p.id ? <span className="pr-spinner pr-spinner-sm" /> : <TrashIcon />}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {!isSearching && !loading && !fetchError && totalPages > 1 && (
            <div className="pr-pagination">
              <button
                className="pr-page-btn"
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1}
              >
                ← Anterior
              </button>
              <span className="pr-page-info">
                Página {page} de {totalPages} · {totalCount} producto{totalCount !== 1 ? 's' : ''}
              </span>
              <button
                className="pr-page-btn"
                onClick={() => goToPage(page + 1)}
                disabled={page >= totalPages}
              >
                Siguiente →
              </button>
            </div>
          )}
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════ */}
      {/* ── TRASH TAB ── */}
      {/* ══════════════════════════════════════════════════════════════ */}
      {activeTab === 'trash' && (
        <>
          <div className="pr-trash-info">
            <TrashInfoIcon />
            <p>
              Los productos se eliminan permanentemente después de {RETENTION_DAYS} días.
              Durante este período <strong>siguen contando para tu límite de plan</strong>.
            </p>
          </div>

          <div className="pr-table-wrap">
            {loadingTrash ? (
              <div className="pr-loading">
                <span className="pr-spinner" />
                <p>Cargando papelera…</p>
              </div>
            ) : trashError ? (
              <div className="pr-error-box">
                <p>{trashError}</p>
                <button className="pr-btn-retry" onClick={fetchTrashed}>Reintentar</button>
              </div>
            ) : trashed.length === 0 ? (
              <div className="pr-empty">
                <TrashEmptyIcon />
                <p className="pr-empty-title">Papelera vacía</p>
                <p className="pr-empty-sub">Los productos eliminados aparecerán aquí durante {RETENTION_DAYS} días.</p>
              </div>
            ) : (
              <table className="pr-table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>N° CAS</th>
                    <th>Clasificación IMDG</th>
                    <th>Tiempo restante</th>
                    <th style={{ width: 160 }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {trashed.map((p) => {
                    const days = p.deleted_at ? daysUntilDeletion(p.deleted_at) : 0;
                    return (
                      <tr key={p.id} className="pr-tr-archived">
                        <td className="pr-td-name pr-td-archived">{p.name}</td>
                        <td className="pr-td-mono">{p.cas_number || <span className="pr-empty-cell">—</span>}</td>
                        <td>
                          <div className="pr-badges">
                            {p.sga_classes.length === 0
                              ? <span className="pr-empty-cell">—</span>
                              : p.sga_classes.map((id) => {
                                  const cls = SGA_CLASSES.find((s) => s.id === id);
                                  return cls ? (
                                    <img key={id} src={cls.svg} alt={cls.label} title={cls.label}
                                      className="pr-sga-badge-img pr-sga-archived" width="28" height="28" />
                                  ) : null;
                                })}
                          </div>
                        </td>
                        <td>
                          <span className={`pr-days-badge ${days <= 1 ? 'urgent' : days <= 3 ? 'warning' : ''}`}>
                            {days === 0 ? 'Expira hoy' : `Se elimina en ${days} día${days !== 1 ? 's' : ''}`}
                          </span>
                          {p.deleted_at && (
                            <div style={{ color: '#ef4444', fontSize: '0.72rem', marginTop: 4, fontWeight: 600 }}>
                              Se eliminará el {deletionDateDDMMYYYY(p.deleted_at)}
                            </div>
                          )}
                        </td>
                        <td>
                          <div className="pr-actions">
                            <button
                              className="pr-btn-restore"
                              onClick={() => handleRestore(p)}
                              disabled={restoringId === p.id}
                              title="Restaurar producto"
                            >
                              {restoringId === p.id ? <span className="pr-spinner pr-spinner-sm" /> : <RestoreIcon />}
                              Restaurar
                            </button>
                            {!isViewer && (
                              <button
                                className="pr-btn-icon pr-btn-perm-delete"
                                onClick={() => handlePermanentDelete(p)}
                                disabled={permDeletingId === p.id}
                                title="Eliminar definitivamente"
                              >
                                {permDeletingId === p.id ? <span className="pr-spinner pr-spinner-sm" /> : <TrashIcon />}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* ── Toast ── */}
      {toast && <div className="pr-toast">{toast}</div>}

      {/* ── Soft delete confirmation modal ── */}
      {confirmTrashProduct && (
        <div className="pr-overlay" onClick={(e) => e.target === e.currentTarget && setConfirmTrashProduct(null)}>
          <div className="pr-confirm-modal">
            <div className="pr-confirm-icon pr-confirm-icon-trash">
              <TrashIcon />
            </div>
            <h4>Mover a papelera</h4>
            <p className="pr-confirm-name">"{confirmTrashProduct.name}"</p>
            <p className="pr-confirm-msg">
              Este producto se eliminará definitivamente el{' '}
              <strong>{deletionDateLabel(new Date().toISOString())}</strong>.
              Durante este período seguirá contando para tu límite de plan.
            </p>
            <div className="pr-confirm-actions">
              <button className="pr-btn-cancel" onClick={() => setConfirmTrashProduct(null)}>
                Cancelar
              </button>
              <button className="pr-btn-confirm-trash" onClick={confirmSoftDelete}>
                <TrashIcon /> Mover a papelera
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Permanent delete confirmation modal ── */}
      {confirmPermProduct && (
        <div className="pr-overlay" onClick={(e) => e.target === e.currentTarget && setConfirmPermProduct(null)}>
          <div className="pr-confirm-modal">
            <div className="pr-confirm-icon pr-confirm-icon-perm">
              <DangerIcon />
            </div>
            <h4>Eliminar definitivamente</h4>
            <p className="pr-confirm-name">"{confirmPermProduct.name}"</p>
            <p className="pr-confirm-msg">
              Esta acción no se puede deshacer. El producto y su Hoja de Seguridad serán eliminados permanentemente.
            </p>
            <div className="pr-confirm-actions">
              <button className="pr-btn-cancel" onClick={() => setConfirmPermProduct(null)}>
                Cancelar
              </button>
              <button className="pr-btn-confirm-perm" onClick={confirmPermanentDelete}>
                Eliminar definitivamente
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit/Add product modal ── */}
      {modalOpen && (
        <div className="pr-overlay" onClick={(e) => e.target === e.currentTarget && closeModal()}>
          <div className="pr-modal">
            <div className="pr-modal-header">
              <h4>{editing ? 'Editar Producto' : 'Agregar Producto'}</h4>
              <button className="pr-modal-close" onClick={closeModal}><CloseIcon /></button>
            </div>

            <form className="pr-form" onSubmit={handleSave}>
              <div className="pr-form-grid">
                {/* Name */}
                <div className="pr-field pr-field-full">
                  <label>Nombre del producto <span className="pr-required">*</span></label>
                  <input
                    type="text"
                    placeholder="ej. Ácido Sulfúrico"
                    value={form.name}
                    onChange={(e) => handleField('name', e.target.value)}
                    required
                    autoFocus
                  />
                </div>

                {/* CAS */}
                <div className="pr-field">
                  <label>Número CAS</label>
                  <input
                    type="text"
                    placeholder="ej. 7664-93-9"
                    value={form.cas_number ?? ''}
                    onChange={(e) => handleField('cas_number', e.target.value)}
                  />
                </div>

                {/* UN/NA */}
                <div className="pr-field">
                  <label>Número UN/NA <span className="pr-field-hint">4 dígitos, opcional</span></label>
                  <input
                    type="text"
                    placeholder="ej. 1830"
                    maxLength={4}
                    value={form.un_number ?? ''}
                    onChange={(e) => handleField('un_number', e.target.value.replace(/\D/g, '').slice(0, 4))}
                  />
                </div>

                {/* Supplier */}
                <div className="pr-field">
                  <label>Proveedor</label>
                  <input
                    type="text"
                    placeholder="ej. Merck"
                    value={form.provider ?? ''}
                    onChange={(e) => handleField('provider', e.target.value)}
                  />
                </div>
              </div>

              {/* Reactive hazards */}
              <div className="pr-field pr-field-full">
                <label>10.3 POSIBILIDADES DE REACCIONES PELIGROSAS</label>
                <textarea
                  rows={3}
                  placeholder="ej. Puede reaccionar violentamente con agentes oxidantes fuertes..."
                  value={form.reactive_hazards ?? ''}
                  onChange={(e) => handleField('reactive_hazards', e.target.value)}
                />
              </div>

              {/* Incompatible materials */}
              <div className="pr-field pr-field-full">
                <label>10.5 MATERIALES INCOMPATIBLES</label>
                <textarea
                  rows={3}
                  placeholder="ej. Ácidos fuertes, bases fuertes, agentes oxidantes..."
                  value={form.incompatible_materials ?? ''}
                  onChange={(e) => handleField('incompatible_materials', e.target.value)}
                />
              </div>

              {/* SGA Classes */}
              <div className="pr-field pr-field-full">
                <label>
                  Clasificación IMDG
                  {form.sga_classes.length === 0 && (
                    <span style={{ color: '#ef4444', fontSize: '0.75rem', fontWeight: 400, marginLeft: 8 }}>
                      * Requerido para evaluación de compatibilidad
                    </span>
                  )}
                </label>
                <div className="pr-sga-grid">
                  {SGA_CLASSES.map((cls) => {
                    const checked = form.sga_classes.includes(cls.id);
                    return (
                      <label
                        key={cls.id}
                        className={`pr-sga-item ${checked ? 'checked' : ''}`}
                        style={checked ? { borderColor: cls.color, background: cls.bg } : {}}
                      >
                        <input type="checkbox" checked={checked} onChange={() => toggleSGA(cls.id)} />
                        <img src={cls.svg} alt={cls.label} className="pr-sga-picto" width="56" height="56" />
                        <span className="pr-sga-label">{cls.label}</span>
                      </label>
                    );
                  })}
                </div>
                {form.sga_classes.includes('clase8') && (
                  <div style={{ marginTop: 12 }}>
                    <label>Subcategoría de Clase 8 (corrosivos)</label>
                    <select
                      value={form.clase8_subcategoria || ''}
                      onChange={(e) => setForm(f => ({ ...f, clase8_subcategoria: e.target.value || null }))}
                      style={{
                        background: '#0f172a',
                        border: '1px solid #334155',
                        borderRadius: 9,
                        padding: '11px 14px',
                        fontSize: '0.9rem',
                        color: '#e2e8f0',
                        outline: 'none',
                        width: '100%',
                      }}
                    >
                      <option value="">— Seleccionar —</option>
                      {CLASE8_SUBCATEGORIAS.map(sub => (
                        <option key={sub.value} value={sub.value}>{sub.label}</option>
                      ))}
                    </select>
                  </div>
                )}
                {(form.sga_classes.includes('clase6') || form.coshh_h_phrases.length > 0) && (
                  <div style={{ marginTop: 16, padding: 12, background: '#0f172a', border: '1px solid #334155', borderRadius: 10 }}>
                    <label style={{ fontWeight: 600 }}>Higiene Industrial — Peligrosidad química (COSHH Essentials)</label>
                    <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 4, marginBottom: 10 }}>
                      Marca las frases H que apliquen a esta sustancia. El sistema calculará automáticamente su categoría de peligrosidad (A-E) para evaluar el riesgo por inhalación en las tareas donde se use.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 6, maxHeight: 220, overflowY: 'auto' }}>
                      {COSHH_H_PHRASES.map(p => {
                        const checked = form.coshh_h_phrases.includes(p.code);
                        return (
                          <label key={p.code} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', fontSize: 12, color: '#cbd5e1', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => {
                                setForm(f => ({
                                  ...f,
                                  coshh_h_phrases: checked
                                    ? f.coshh_h_phrases.filter(c => c !== p.code)
                                    : [...f.coshh_h_phrases, p.code],
                                }));
                              }}
                              style={{ marginTop: 2, flexShrink: 0 }}
                            />
                            <span style={{ flex: 1, minWidth: 0, overflowWrap: 'break-word' }}><strong>{p.code}</strong> ({p.categoria}) — {p.text}</span>
                          </label>
                        );
                      })}
                    </div>
                    {form.coshh_h_phrases.length > 0 && (
                      <p style={{ marginTop: 8, fontSize: 12, color: '#60a5fa' }}>
                        Categoría de peligrosidad calculada: <strong>{calcularPeligrosidad(form.coshh_h_phrases)}</strong>
                      </p>
                    )}

                    <div style={{ marginTop: 12 }}>
                      <label style={{ fontSize: 12 }}>Estado físico</label>
                      <select
                        value={form.coshh_estado_fisico}
                        onChange={e => setForm(f => ({ ...f, coshh_estado_fisico: e.target.value }))}
                        style={{ width: '100%', marginTop: 4, background: '#1e293b', border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0', padding: '6px 10px', fontSize: 13 }}
                      >
                        <option value="">— Seleccionar —</option>
                        <option value="liquido">Líquido</option>
                        <option value="solido">Sólido</option>
                      </select>
                    </div>

                    {form.coshh_estado_fisico === 'liquido' && (
                      <div style={{ marginTop: 10 }}>
                        <label style={{ fontSize: 12 }}>Punto de ebullición (°C)</label>
                        <input
                          type="number"
                          value={form.coshh_punto_ebullicion}
                          onChange={e => setForm(f => ({ ...f, coshh_punto_ebullicion: e.target.value }))}
                          style={{ width: '100%', marginTop: 4, background: '#1e293b', border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0', padding: '6px 10px', fontSize: 13 }}
                          placeholder="ej. 78"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* SDS Upload */}
              <div className="pr-field pr-field-full">
                <label>Hoja de Seguridad (SDS)</label>
                <div className="pr-sds-section">
                  {editing?.sds_url && !sdsFile && (
                    <div className="pr-sds-current">
                      <PdfIcon />
                      <span className="pr-sds-filename">PDF cargado</span>
                      <EnlaceArchivo bucket="safety-sheets" valor={editing.sds_url} className="pr-sds-view-link">
                        Ver
                      </EnlaceArchivo>
                    </div>
                  )}
                  {sdsFile && (
                    <div className="pr-sds-current">
                      <PdfIcon />
                      <span className="pr-sds-filename">{sdsFile.name}</span>
                      <button
                        type="button"
                        className="pr-sds-remove"
                        onClick={() => { setSdsFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                      >
                        Quitar
                      </button>
                    </div>
                  )}
                  <label className="pr-sds-upload-btn">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,application/pdf"
                      onChange={(e) => setSdsFile(e.target.files?.[0] ?? null)}
                      style={{ display: 'none' }}
                    />
                    <UploadIcon />
                    <span>{(sdsFile || editing?.sds_url) ? 'Cambiar PDF' : 'Subir PDF'}</span>
                  </label>
                </div>
              </div>

              {error && <p className="pr-error">{error}</p>}

              <div className="pr-modal-footer">
                <button type="button" className="pr-btn-cancel" onClick={closeModal}>
                  Cancelar
                </button>
                <button type="submit" className="pr-btn-save" disabled={saving}>
                  {saving ? <span className="pr-spinner pr-spinner-sm" /> : null}
                  {saving ? 'Guardando…' : (editing ? 'Guardar cambios' : 'Agregar')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Icons ────────────────────────────────────────────────────────────────────

function DownloadIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}
function EditIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}
function TrashIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" />
    </svg>
  );
}
function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
function EmptyBoxIcon() {
  return (
    <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
      <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
      <line x1="12" y1="22.08" x2="12" y2="12" />
    </svg>
  );
}
function EyeIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
function AlertTriangleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}
function PdfIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="12" y2="17" />
    </svg>
  );
}
function UploadIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="16 16 12 12 8 16" />
      <line x1="12" y1="12" x2="12" y2="21" />
      <path d="M20.39 18.39A5 5 0 0018 9h-1.26A8 8 0 103 16.3" />
    </svg>
  );
}
function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}
function RestoreIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="1 4 1 10 7 10" />
      <path d="M3.51 15a9 9 0 102.13-9.36L1 10" />
    </svg>
  );
}
function ProductsTabIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
    </svg>
  );
}
function TrashTabIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
    </svg>
  );
}
function TrashEmptyIcon() {
  return (
    <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" />
    </svg>
  );
}
function TrashInfoIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, marginTop: 1 }}>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}
function DangerIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}
